import { computed } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type { AccordionEntity, LegacyAccordion, NarrowWidgetPositionLegacy } from '@sites/entity-core';
import { accordionLegacyDefaultV0Schema } from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type AccordionFormModel = AccordionEntity;
export type AccordionDataModel = AccordionEntity['data'];
export type AccordionItem = AccordionDataModel['accordionList'][number];

/**
 * Signal-form view of the accordion widget. **Single placement, single factory** — unlike
 * button/video/text, accordion has no zero-block variant at all (`params: defaultWidgetParams`
 * unmodified), so there is no dispatcher and no per-variant assembler pair.
 *
 * **No animation plugin, no position plugin** — accordion is the smallest settings surface
 * migrated so far (no animation field exists on the entity at all), and its `position` grid is
 * the NARROW shape (`NarrowWidgetPositionLegacy` — no tablet fields, no `mobileWidth`), which has
 * no existing generic plugin to install profitably for a single consumer — inline accessors here,
 * same choice `text`'s form makes for its own non-standard position type.
 *
 * **Field accessors are FieldTree references** — exposed via getters so the FieldTree paths
 * materialise lazily (after `syncFromEntity` populates the model). Convenience `setX(v)` methods
 * are sugar over `field().value.set(v)`; `accordionList` adds `addAccordionItem`/
 * `removeAccordionItem`/`moveAccordionItem`/`updateAccordionItem`/`accordionAt(i)` sugar, mirroring
 * button's `buttons` array pattern.
 */
export function createAccordionForm(options?: BaseLegacyEntityFormOptions<LegacyAccordion>) {
    const base = createBaseLegacyEntityForm<LegacyAccordion>((schemaPath) => {
        validateStandardSchema(schemaPath, accordionLegacyDefaultV0Schema);
        configureCommonReadonly(schemaPath);
    }, options);

    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<AccordionEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        visibilityPlugin(() => _raw.data),
        {
            // --- accordionList array ---
            get accordionList() {
                return _raw.data.accordionList;
            },
            /** Current list snapshot as a computed signal — template-friendly sugar. */
            accordionItems: computed<AccordionItem[]>(() => _raw.data.accordionList?.().value() ?? []),
            setAccordionList: (value: AccordionItem[]) => {
                _raw.data.accordionList().value.set(value);
            },
            accordionAt: (index: number): FieldTree<AccordionItem> | undefined => {
                return (_raw.data.accordionList as unknown as Record<number, FieldTree<AccordionItem>>)[index];
            },
            addAccordionItem: (item: AccordionItem) => {
                _raw.data.accordionList().value.update((arr) => [...arr, item]);
            },
            removeAccordionItem: (index: number) => {
                _raw.data.accordionList().value.update((arr) => arr.filter((_, i) => i !== index));
            },
            moveAccordionItem: (from: number, to: number) => {
                _raw.data.accordionList().value.update((arr) => arrayMove(arr, from, to));
            },
            updateAccordionItem: (index: number, patch: Partial<AccordionItem>) => {
                _raw.data
                    .accordionList()
                    .value.update((arr) => arr.map((el, i) => (i === index ? { ...el, ...patch } : el)));
            },

            // --- Appearance ---
            get horizontalAlign() {
                return _raw.data.horizontalAlign;
            },
            setHorizontalAlign: (value: AccordionDataModel['horizontalAlign']) => {
                _raw.data.horizontalAlign().value.set(value);
            },
            get iconType() {
                return _raw.data.iconType;
            },
            setIconType: (value: AccordionDataModel['iconType']) => {
                _raw.data.iconType().value.set(value);
            },
            get iconColor() {
                return _raw.data.iconColor;
            },
            get themeIconColor() {
                return _raw.data.themeIconColor;
            },
            /** Mirrors the control's two-field theme+resolved-colour setter (`setIconColor`). */
            setIconColor: (value: string, presetName: string | null) => {
                _raw.data.themeIconColor().value.set(presetName);
                _raw.data.iconColor().value.set(value);
            },
            get isIndent() {
                return _raw.data.isIndent;
            },
            setIsIndent: (value: boolean) => {
                _raw.data.isIndent().value.set(value);
            },
            get indentColor() {
                return _raw.data.indentColor;
            },
            get themeIndentColor() {
                return _raw.data.themeIndentColor;
            },
            setIndentColor: (value: string, presetName: string | null) => {
                _raw.data.themeIndentColor().value.set(presetName);
                _raw.data.indentColor().value.set(value);
            },
            get isAutoCollapse() {
                return _raw.data.isAutoCollapse;
            },
            setIsAutoCollapse: (value: boolean) => {
                _raw.data.isAutoCollapse().value.set(value);
            },

            // --- Sync + position ---
            get isSynchronizeStyle() {
                return _raw.data.isSynchronizeStyle;
            },
            /**
             * Style-synchronisation flag with the legacy-null rule applied: very old widgets
             * persisted `null`, which the FormGroup editor treated as "off" — truthy check on
             * purpose (same convention as button/text).
             */
            isSynchronized: computed<boolean>(() => Boolean(_raw.data.isSynchronizeStyle?.().value())),
            setIsSynchronizeStyle: (value: boolean) => {
                _raw.data.isSynchronizeStyle().value.set(value);
            },
            get position() {
                return _raw.data.position;
            },
            setPosition: (value: NarrowWidgetPositionLegacy) => {
                _raw.data.position().value.set(value);
            },
        }
    );
}

export type AccordionForm = ReturnType<typeof createAccordionForm>;
